import React, { useRef, useState } from 'react';
import { IconScout } from '@/components/ui/images/IconScout';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { colors } from '@/assets/styles/colors';


export interface InfoBubbleProps {
  /** Tooltip body text shown on hover */
  text: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Inline info icon that shows a tooltip on hover.
 * Renders a question-circle icon; hovering reveals a Tooltip with `text`.
 * Uses `strategy="absolute"` so the tooltip is positioned relative to the icon,
 * avoiding all viewport/transform issues.
 *
 * Layout strategy:
 * - Layer 1 (outer span): inline-block, height:0, width:20px. Reserves horizontal
 *   space but contributes zero vertical height → no line box inflation.
 * - Layer 2 (inner span): position:absolute, 20×20px, centered via top:-10px.
 *   Contains the icon, white background mask, and event handlers.
 * - Tooltip: uses strategy="absolute" → position:absolute relative to Layer 2.
 */
export const InfoBubble: React.FC<InfoBubbleProps> = ({
  text,
  className = '',
  'data-testid': dataTestId,
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span
      className={`inline-block ml-1 ${className}`}
      style={{
        width: '20px',
        height: '0',
        overflow: 'visible',
        position: 'relative',
        verticalAlign: 'middle',
        zIndex: 1,
      }}
    >
      <span
        ref={anchorRef}
        style={{
          position: 'absolute',
          top: '-11px',
          left: '0',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'help',
        }}
        tabIndex={0}
        aria-label={text}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        data-testid={dataTestId ?? 'info-bubble'}
      >

        <IconScout
          name="questionCircle"
          size="lg"
          color={colors.primary.auxiliar}
          data-testid="info-bubble-icon"
        />
        {showTooltip && (
          <Tooltip
            body={text}
            anchorRef={anchorRef}
            strategy="absolute"
            placement="top"
            maxWidth={300}
          />
        )}
      </span>
    </span>
  );
};

export default InfoBubble;
